import { action, computed, observable, makeAutoObservable } from 'mobx';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { apiCommon } from 'src/api/ApiCommon';
import { FormInputState, FormModel, group, Result, ResultValue, ResultError } from 'src_common/common/mobx-utils/Form';
import { InputStatus } from 'src/components/Input/InputStatus';
import {
    validateExpectHexColor,
    validateExpectNumber,
    validateRequire,
    validateStringLength20,
    validateStringLength255,
} from 'src/api/utils/formValidators';
import { UploadImageState } from '../UploadImageState/UploadImageState';
import { HeroWidgetModelType } from 'src/api/config/hero_widget/get_hero_widget';
import { ParamsType } from 'src/api_openapi/generated/openapi_website_cms_createHeroWidget';
import { TextareaStatus } from 'src/components/FormComponents/Textarea/TextareaStatus';

type HeroWidgetBodyType = ParamsType['body'];

interface HeroWidgetLoading {
    type: 'loading';
}

interface HeroWidgetReady {
    type: 'ready';
    value: Array<HeroWidgetModelType>;
}

interface HeroWidgetError {
    type: 'error';
    value: string;
}

type HeroWidgetContentResponse = HeroWidgetLoading | HeroWidgetReady | HeroWidgetError;
type PageType = 'edit' | 'add';

interface HeroWidgetType {
    eventId: number | null;
    marketId: number | null;
    title: string;
    subtitle: string;
    headerBgColor: string;
    promoBgColor: string;
    dateFrom: string;
    dateTo: string;
    tcTitle: string;
    tcText: string;
    isActiveWidget: boolean;
    isActivePromo: boolean;
}

interface HeroWidgetValidType {
    eventId: number;
    marketId: number;
    title: string;
    subtitle: string;
    headerBgColor: string;
    promoBgColor: string;
    dateFrom: string;
    dateTo: string;
    tcTitle: string;
    tcText: string;
    isActiveWidget: boolean;
    isActivePromo: boolean;
    displayOrder: number;
}

export class HeroWidgetState {
    @observable private readonly heroWidgetsResource: Resource<Array<HeroWidgetModelType>>;
    @observable private page: string = 'add';
    @observable private isActiveFlagChanged: boolean = false;
    @observable public editedHeroWidget: HeroWidgetModelType | null = null;
    @observable public isLoading: boolean = false;
    @observable public errorMessage: string = '';
    @observable public selectedHeroWidgetType: 'image' | 'video' = 'image';
    @observable imgWarningMessage: string | null = null;
    @observable imgErrorMessage: string | null = null;

    @observable onGoingUploadFile: File | null = null;

    readonly eventIdModel: FormModel<number | null>;
    readonly eventId: FormInputState<string>;
    readonly eventIdStatus: InputStatus;
    readonly marketIdModel: FormModel<number | null>;
    readonly marketId: FormInputState<string>;
    readonly marketIdStatus: InputStatus;
    readonly titleModel: FormModel<string>;
    readonly title: FormInputState<string>;
    readonly titleStatus: InputStatus;
    readonly subtitleModel: FormModel<string>;
    readonly subtitle: FormInputState<string>;
    readonly subtitleStatus: InputStatus;
    readonly headerBgColorModel: FormModel<string>;
    readonly headerBgColor: FormInputState<string>;
    readonly headerBgColorStatus: InputStatus;
    readonly promoBgColorModel: FormModel<string>;
    readonly promoBgColor: FormInputState<string>;
    readonly promoBgColorStatus: InputStatus;
    readonly isActiveWidgetModel: FormModel<boolean>;
    readonly isActiveWidget: FormInputState<boolean>;
    readonly isActiveWidgetStatus: InputStatus;
    readonly isActivePromoModel: FormModel<boolean>;
    readonly isActivePromo: FormInputState<boolean>;
    readonly isActivePromoStatus: InputStatus;
    readonly dateFromModel: FormModel<string>;
    readonly dateFrom: FormInputState<string>;
    readonly dateFromStatus: InputStatus;
    readonly dateToModel: FormModel<string>;
    readonly dateTo: FormInputState<string>;
    readonly dateToStatus: InputStatus;
    readonly form: FormModel<HeroWidgetValidType>;
    readonly tcTitleModel: FormModel<string>;
    readonly tcTitle: FormInputState<string>;
    readonly tcTitleStatus: InputStatus;
    readonly tcTextModel: FormModel<string>;
    readonly tcText: FormInputState<string>;
    readonly tcTextStatus: TextareaStatus;

    public uploadHeaderImgSmall: UploadImageState;
    public uploadHeaderImgMedium: UploadImageState;
    public uploadHeaderImgBig: UploadImageState;
    public uploadPromoImgSmall: UploadImageState;
    public uploadPromoImgMedium: UploadImageState;
    public uploadPromoImgBig: UploadImageState;

    constructor() {
        this.uploadHeaderImgSmall = new UploadImageState('hero-widget/header/small');
        this.uploadHeaderImgMedium = new UploadImageState('hero-widget/header/medium');
        this.uploadHeaderImgBig = new UploadImageState('hero-widget/header/big');

        this.uploadPromoImgSmall = new UploadImageState('hero-widget/promo/small');
        this.uploadPromoImgMedium = new UploadImageState('hero-widget/promo/medium');
        this.uploadPromoImgBig = new UploadImageState('hero-widget/promo/big');

        this.heroWidgetsResource = new Resource(async (): Promise<Array<HeroWidgetModelType>> => {
            this.isLoading = true;
            const response = await apiCommon.get_hero_widget.run({});
            this.isLoading = false;
            return response;
        });

        this.eventId = new FormInputState('', false);
        this.eventIdModel = this.eventId.map(validateRequire).map(validateExpectNumber);
        this.eventIdStatus = new InputStatus(this.eventIdModel);

        this.marketId = new FormInputState('', false);
        this.marketIdModel = this.marketId.map(validateRequire).map(validateExpectNumber);
        this.marketIdStatus = new InputStatus(this.marketIdModel);

        this.title = new FormInputState('', false);
        this.titleModel = this.title.map(validateRequire).map(validateStringLength255);
        this.titleStatus = new InputStatus(this.titleModel);

        this.subtitle = new FormInputState('', false);
        this.subtitleModel = this.subtitle.map(validateStringLength255);
        this.subtitleStatus = new InputStatus(this.subtitleModel);

        this.headerBgColor = new FormInputState('', false);
        this.headerBgColorModel = this.headerBgColor.map(validateExpectHexColor);
        this.headerBgColorStatus = new InputStatus(this.headerBgColorModel);

        this.promoBgColor = new FormInputState('', false);
        this.promoBgColorModel = this.promoBgColor.map(validateExpectHexColor);
        this.promoBgColorStatus = new InputStatus(this.promoBgColorModel);

        this.dateFrom = new FormInputState('', false);
        this.dateFromModel = this.dateFrom.map(validateRequire);
        this.dateFromStatus = new InputStatus(this.dateFromModel);

        this.dateTo = new FormInputState('', false);
        this.dateToModel = this.dateTo.map(validateRequire);
        this.dateToStatus = new InputStatus(this.dateToModel);

        this.tcTitle = new FormInputState('', false);
        this.tcTitleModel = this.tcTitle.map(validateStringLength20);
        this.tcTitleStatus = new InputStatus(this.tcTitleModel);

        this.tcText = new FormInputState('', false);
        this.tcTextModel = this.tcText.toModel();
        this.tcTextStatus = new TextareaStatus(this.tcTextModel);

        this.isActiveWidget = new FormInputState(false, false);
        this.isActiveWidgetModel = this.isActiveWidget.toModel();
        this.isActiveWidgetStatus = new InputStatus(this.isActiveWidgetModel);

        this.isActivePromo = new FormInputState(false, false);
        this.isActivePromoModel = this.isActivePromo.toModel();
        this.isActivePromoStatus = new InputStatus(this.isActivePromoModel);

        this.tcText = new FormInputState('', false);
        this.tcTextModel = this.tcText.toModel();
        this.tcTextStatus = new TextareaStatus(this.tcTextModel);

        this.form = group({
            eventId: this.eventIdModel,
            marketId: this.marketIdModel,
            title: this.titleModel,
            subtitle: this.subtitleModel,
            headerBgColor: this.headerBgColorModel,
            promoBgColor: this.promoBgColorModel,
            dateFrom: this.dateFromModel,
            dateTo: this.dateToModel,
            tcTitle: this.tcTitleModel,
            tcText: this.tcTextModel,
            isActiveWidget: this.isActiveWidgetModel,
            isActivePromo: this.isActivePromoModel,
        }).map((item: HeroWidgetType): Result<HeroWidgetValidType> => {
            if (item.eventId === null || item.marketId === null) {
                return new ResultError('event id nad market id field is required');
            }
            return new ResultValue({
                eventId: item.eventId,
                marketId: item.marketId,
                title: item.title,
                subtitle: item.subtitle,
                headerBgColor: item.headerBgColor,
                promoBgColor: item.promoBgColor,
                dateFrom: item.dateFrom,
                dateTo: item.dateTo,
                tcTitle: item.tcTitle,
                tcText: item.tcText,
                isActiveWidget: item.isActiveWidget,
                isActivePromo: item.isActivePromo,
            });
        });
        makeAutoObservable(this);
    }

    @action public handleCancel = (): void => {
        if (this.isEditMode && this.editedHeroWidget !== null) {
            this.setEditedHeroWidget(this.editedHeroWidget);
        } else {
            this.form.reset();
            this.resetImages();
        }
    };

    @action public resetForm = (): void => {
        this.setPage('add');
        this.editedHeroWidget = null;
        this.form.reset();
        this.resetImages();
    };

    @action public setPage = (value: PageType): void => {
        this.page = value;
    };

    @action public setActiveWidgetFlag = async (heroWidget: HeroWidgetModelType) => {
        this.isActiveFlagChanged = true;
        this.isLoading = true;

        const payload: HeroWidgetModelType = {
            ...heroWidget,
            is_active_widget: !heroWidget.is_active_widget,
        };

        const submitResult = await this.updateHeroWidget(payload, heroWidget.id);

        if (submitResult) {
            await this.heroWidgetsResource.refresh();
        } else {
            this.errorMessage = 'Unknown error. Something went wrong.';
        }
        this.isActiveFlagChanged = false;
        this.isLoading = false;
    };

    @action public setActivePromoFlag = async (heroWidget: HeroWidgetModelType) => {
        this.isActiveFlagChanged = true;
        this.isLoading = true;

        const payload: HeroWidgetModelType = {
            ...heroWidget,
            is_active_promo: !heroWidget.is_active_promo,
        };

        const submitResult = await this.updateHeroWidget(payload, heroWidget.id);

        if (submitResult) {
            await this.heroWidgetsResource.refresh();
        } else {
            this.errorMessage = 'Unknown error. Something went wrong.';
        }
        this.isActiveFlagChanged = false;
        this.isLoading = false;
    };

    @action public onRemove = async (id: number): Promise<void> => {
        const removeResult = await apiCommon.delete_hero_widget.run({ id });

        if (removeResult) {
            await this.heroWidgetsResource.refresh();

            this.form.reset();
        } else {
            this.errorMessage = 'Unknown error. Something went wrong.';
        }
    };

    @action public handleSubmit = async () => {
        this.isLoading = true;
        this.errorMessage = '';
        this.form.setAsVisited();
        const result = this.form.result;

        if (result instanceof ResultValue && this.heroWidgets.type === 'ready') {
            const topElem = this.heroWidgetList[0] ?? null;
            const display_order =
                this.isEditMode && this.editedHeroWidget !== null
                    ? this.editedHeroWidget.display_order
                    : topElem !== null
                      ? topElem.display_order + 1
                      : 1;

            const payload: HeroWidgetBodyType = {
                event_id: result.value.eventId,
                market_id: result.value.marketId,
                title: result.value.title,
                subtitle: result.value.subtitle,
                header_bg_color: result.value.headerBgColor,
                promo_bg_color: result.value.promoBgColor,
                date_from: result.value.dateFrom,
                date_to: result.value.dateTo,
                tc_title: result.value.tcTitle,
                tc_text: result.value.tcText,
                header_img_small: this.uploadHeaderImgSmall.imgUrl,
                header_img_medium: this.uploadHeaderImgMedium.imgUrl,
                header_img_big: this.uploadHeaderImgBig.imgUrl,
                promo_img_small: this.uploadPromoImgSmall.imgUrl,
                promo_img_medium: this.uploadPromoImgMedium.imgUrl,
                promo_img_big: this.uploadPromoImgBig.imgUrl,
                is_active_widget: result.value.isActiveWidget,
                is_active_promo: result.value.isActivePromo,
                display_order: display_order,
            };

            const submitResult =
                this.isEditMode && this.editedHeroWidget !== null
                    ? await this.updateHeroWidget(payload, this.editedHeroWidget.id)
                    : await this.createHeroWidget(payload);

            if (submitResult) {
                await this.heroWidgetsResource.refresh();
                this.resetForm();
            } else {
                this.errorMessage = 'Unknown error. Something went wrong.';
            }
        }

        this.isLoading = false;
    };

    @action private createHeroWidget = async (heroWidget: HeroWidgetBodyType): Promise<number | Error | null> => {
        let response: number | Error | null;
        try {
            response = await apiCommon.post_create_hero_widget.run({ data: heroWidget });
        } catch (error) {
            console.error(error);
            return null;
        }
        return response;
    };

    @action private updateHeroWidget = async (
        heroWidget: HeroWidgetBodyType,
        id: number
    ): Promise<number | Error | null> => {
        let response: number | Error | null;

        try {
            response = await apiCommon.put_update_hero_widget.run({ data: heroWidget, id: id });
        } catch (error) {
            console.error(error);
            return null;
        }
        return response;
    };

    @action public setEditedHeroWidget = (heroWidget: HeroWidgetModelType): void => {
        this.editedHeroWidget = heroWidget;
        if (this.heroWidgets.type === 'ready') {
            const eventId = heroWidget.event_id ?? '';
            const marketId = heroWidget.market_id ?? '';
            this.eventId.setValue(eventId.toString());
            this.marketId.setValue(marketId.toString());
            this.title.setValue(heroWidget.title ?? '');
            this.subtitle.setValue(heroWidget.subtitle ?? '');
            this.headerBgColor.setValue(heroWidget.header_bg_color ?? '');
            this.promoBgColor.setValue(heroWidget.promo_bg_color ?? '');
            this.dateFrom.setValue(heroWidget.date_from);
            this.dateTo.setValue(heroWidget.date_to);
            this.tcText.setValue(heroWidget.tc_text ?? '');
            this.tcTitle.setValue(heroWidget.tc_title ?? '');
            this.isActiveWidget.setValue(heroWidget.is_active_widget);
            this.isActivePromo.setValue(heroWidget.is_active_promo);
            this.setEditImages(heroWidget);
        }
    };

    moveItemUp = async (id: number): Promise<void> => {
        const data = {
            id,
            direction: 'up',
        };

        const result = await apiCommon.post_order_hero_widget.run({ data });

        if (result) {
            await this.heroWidgetsResource.refresh();
            this.resetForm();
        } else {
            this.errorMessage = 'Unknown error. Something went wrong.';
        }
    };

    moveItemDown = async (id: number): Promise<void> => {
        const data = {
            id,
            direction: 'down',
        };

        const result = await apiCommon.post_order_hero_widget.run({ data });

        if (result) {
            await this.heroWidgetsResource.refresh();
            this.resetForm();
        } else {
            this.errorMessage = 'Unknown error. Something went wrong.';
        }
    };

    @action resetImages = (): void => {
        this.uploadHeaderImgSmall.handleResetImage();
        this.uploadHeaderImgMedium.handleResetImage();
        this.uploadHeaderImgBig.handleResetImage();
        this.uploadPromoImgSmall.handleResetImage();
        this.uploadPromoImgMedium.handleResetImage();
        this.uploadPromoImgBig.handleResetImage();
    };

    @action setEditImages = (heroWidget: HeroWidgetModelType): void => {
        this.uploadHeaderImgSmall.handleChooseImage(heroWidget.header_img_small);
        this.uploadHeaderImgMedium.handleChooseImage(heroWidget.header_img_medium);
        this.uploadHeaderImgBig.handleChooseImage(heroWidget.header_img_big);
        this.uploadPromoImgSmall.handleChooseImage(heroWidget.promo_img_small);
        this.uploadPromoImgMedium.handleChooseImage(heroWidget.promo_img_medium);
        this.uploadPromoImgBig.handleChooseImage(heroWidget.promo_img_big);
    };

    @computed private get heroWidgets(): HeroWidgetContentResponse {
        const heroWidget = this.heroWidgetsResource.get();
        if (heroWidget.type === 'ready' && heroWidget.value !== null) {
            return {
                type: 'ready',
                value: heroWidget.value,
            };
        }
        if (heroWidget.type === 'loading') {
            return {
                type: 'loading',
            };
        }

        return {
            type: 'error',
            value: 'Something went wrong with fetching hero widget info',
        };
    }

    @computed public get heroWidgetList(): Array<HeroWidgetModelType> {
        if (this.heroWidgets.type === 'ready') {
            return this.heroWidgets.value.sort((hero1, hero2) => {
                if (hero1 !== null && hero2 !== null) {
                    if (hero1.display_order > hero2.display_order) {
                        return -1;
                    }
                    if (hero1.display_order < hero2.display_order) {
                        return 1;
                    }
                    return 0;
                }
                return -1;
            });
        }
        return [];
    }

    @computed public get isFormValid(): boolean {
        return this.form.result instanceof ResultValue;
    }

    @computed public get isEditMode(): boolean {
        return this.page === 'edit' || this.isActiveFlagChanged;
    }

    @computed private get parsedStartDate(): number | null {
        return this.dateFrom.value !== null ? Date.parse(this.dateFrom.value) : null;
    }

    @computed private get parsedEndDate(): number | null {
        return this.dateTo.value !== null ? Date.parse(this.dateTo.value) : null;
    }

    @computed public get startDate(): Date | null {
        return this.parsedStartDate === null || isNaN(this.parsedStartDate) ? null : new Date(this.parsedStartDate);
    }

    @computed public get endDate(): Date | null {
        return this.parsedEndDate === null || isNaN(this.parsedEndDate) ? null : new Date(this.parsedEndDate);
    }

    static createForContext(): HeroWidgetState {
        return new HeroWidgetState();
    }
}
